import React, { useState, useEffect, useRef } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { useLocation } from 'react-router-dom';
import './AgoraCall.css'; 

const AgoraCall = () => {
  const APP_ID = "e7251e03f39548869d0a4a054542692c";
  const location = useLocation();
  const { channelName, agoraToken, appId } = location.state || {};

  const [joinState, setJoinState] = useState({ joined: false, loading: false, error: null });
  const [tracks, setTracks] = useState([]);
  const [users, setUsers] = useState({});
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoMuted, setVideoMuted] = useState(false);

  const clientRef = useRef(null);
  const videoContainerRef = useRef(null);

  useEffect(() => {
    clientRef.current = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });

    clientRef.current.on("user-published", handleUserPublished);
    clientRef.current.on("user-unpublished", handleUserUnpublished);
    clientRef.current.on("user-left", handleUserLeft);

    return () => {
      leaveChannel();
    };
  }, []);

  const joinChannel = async () => {
    try {
      setJoinState({ joined: false, loading: true, error: null });

      if (!appId || !agoraToken || !channelName) {
        throw new Error("Missing Agora credentials");
      }

      const uid = await clientRef.current.join(appId, channelName, agoraToken, null);
      const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
      setTracks([audioTrack, videoTrack]);

      await clientRef.current.publish([audioTrack, videoTrack]);

      const localContainer = document.createElement("div");
      localContainer.id = `player-${uid}`;
      localContainer.className = "video-player";
      videoContainerRef.current.appendChild(localContainer);
      videoTrack.play(localContainer);

      setJoinState({ joined: true, loading: false, error: null });

    } catch (error) {
      console.error("Join error:", error);
      setJoinState(prev => ({ ...prev, loading: false, error: error.message }));
      alert(`Failed to join: ${error.message}`);
    }
  };

  const leaveChannel = async () => {
    try {
      tracks.forEach(track => {
        track.stop();
        track.close();
      });

      await clientRef.current.leave();
      setTracks([]);
      setUsers({});
      setAudioMuted(false);
      setVideoMuted(false);
      setJoinState({ joined: false, loading: false, error: null });
      videoContainerRef.current.innerHTML = "";

    } catch (error) {
      console.error("Leave error:", error);
    }
  };

  const handleUserPublished = async (user, mediaType) => {
    await clientRef.current.subscribe(user, mediaType);

    if (mediaType === "video") {
      const remoteContainer = document.createElement("div");
      remoteContainer.id = `player-${user.uid}`;
      remoteContainer.className = "video-player";
      videoContainerRef.current.appendChild(remoteContainer);
      user.videoTrack.play(remoteContainer);
    }

    if (mediaType === "audio") {
      user.audioTrack.play();
    }

    setUsers(prev => ({ ...prev, [user.uid]: user }));
  };

  const handleUserUnpublished = (user, mediaType) => {
    if (mediaType === "video") {
      const player = document.getElementById(`player-${user.uid}`);
      if (player) player.remove();
    }
    setUsers(prev => {
      const updated = { ...prev };
      delete updated[user.uid];
      return updated;
    });
  };

  const handleUserLeft = (user) => {
    const player = document.getElementById(`player-${user.uid}`);
    if (player) player.remove();
    setUsers(prev => {
      const updated = { ...prev };
      delete updated[user.uid];
      return updated;
    });
  };

  const toggleAudio = () => {
    if (tracks[0]) {
      tracks[0].setEnabled(audioMuted);
      setAudioMuted(prev => !prev);
    }
  };

  const toggleVideo = () => {
    if (tracks[1]) {
      tracks[1].setEnabled(videoMuted);
      setVideoMuted(prev => !prev);
    }
  };

  return (
    <div className="main">
      <h1 className="heding">Video Chat</h1>

      {joinState.error && <div className="error-message">Error: {joinState.error}</div>}

      <div className="controls">
        <button onClick={joinChannel} disabled={joinState.joined || joinState.loading}>
          {joinState.loading ? "Connecting..." : "Join Call"}
        </button>
        <button onClick={leaveChannel} disabled={!joinState.joined}>Leave Call</button>
        <button onClick={toggleAudio} disabled={!joinState.joined}>
          {audioMuted ? "Unmute Audio" : "Mute Audio"}
        </button>
        <button onClick={toggleVideo} disabled={!joinState.joined}>
          {videoMuted ? "Turn Video On" : "Turn Video Off"}
        </button>
      </div>

      <div ref={videoContainerRef} className="video-container" />
    </div>
  );
};

export default AgoraCall;